import { useCallback, useEffect, useState } from "react";
import { devError } from "@/lib/log";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, TrendUp, Package, MegaphoneSimple, Camera, Sparkle, Receipt, WhatsappLogo, Compass, ShieldWarning, X, Bell, Check, Lightbulb, ArrowUpRight, Sparkle as SparkleIcon, MagicWand } from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { ImproveResultModal } from "@/components/dashboard/ImproveResultModal";
import { DeltaPill, Empty, Feature, Kpi } from "@/components/dashboard/bits";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [salesStats, setSalesStats] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [subStats, setSubStats] = useState(null);
  const { sucursalId, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [posts, setPosts] = useState([]);
  const [sales, setSales] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertsDismissed, setAlertsDismissed] = useState(false);
  const [waIntents, setWaIntents] = useState([]);
  const [health, setHealth] = useState(null);
  const [insights, setInsights] = useState([]);
  const [insightsTotals, setInsightsTotals] = useState(null);
  const [insightsDismissed, setInsightsDismissed] = useState(false);
  const [improving, setImproving] = useState(null); // {idx, channel} while loading
  const [improveResult, setImproveResult] = useState(null); // {channel, before, after}
  const [autopilot, setAutopilot] = useState(null);
  const [runningAutopilot, setRunningAutopilot] = useState(false);

  const loadAutopilot = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/autopilot/status");
      setAutopilot(data);
    } catch (e) {
      // ignore
    }
  }, [isAdmin]);

  const runAutopilotNow = async () => {
    setRunningAutopilot(true);
    try {
      const { data } = await api.post("/autopilot/run");
      if (data.skipped) {
        toast.info(data.reason || "Piloto omitido");
      } else {
        toast.success(`Piloto mejoró ${data.improved_count} post(s)`);
      }
      await loadAutopilot();
      loadInsights();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "El piloto falló");
    } finally {
      setRunningAutopilot(false);
    }
  };

  const runImprove = async (insight, idx) => {
    setImproving({ idx, channel: insight.channel });
    try {
      const { data } = await api.post("/insights/improve", {
        channel: insight.channel,
      });
      setImproveResult(data);
      toast.success("Copy mejorado · revisa el resultado");
      // Refresh insights — the just-improved post should now rank higher.
      loadInsights();
    } catch (e) {
      const detail = e?.response?.data?.detail || "No se pudo mejorar ahora";
      toast.error(detail);
    } finally {
      setImproving(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const params = sucursalId ? { sucursal_id: sucursalId } : {};
        const [s, p, ss, sl, rs, subs] = await Promise.all([
          api.get("/products/stats", { params }),
          api.get("/posts", { params }),
          api.get("/sales/stats", { params }),
          api.get("/sales", { params }),
          api.get("/referrals/stats"),
          api.get("/subscribers/stats"),
        ]);
        setStats(s.data);
        setPosts(p.data);
        setSalesStats(ss.data);
        setSales(sl.data);
        setReferralStats(rs.data);
        setSubStats(subs.data);
      } catch (e) {
        devError(e);
      }
    })();
  }, [sucursalId]);

  // Admins poll the security-alerts endpoint every 60s. The check is gated
  // on role so we don't hammer the endpoint with a 403 for regular users.
  useEffect(() => {
    if (!isAdmin) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/audit/alerts");
        if (!stop) setAlerts(data?.alerts || []);
      } catch (e) {
        // ignore — not critical for dashboard
      }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [isAdmin]);

  // Load WhatsApp intent leads + settings health for every authenticated user.
  const loadIntents = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/intents", {
        params: { status: "pendiente", limit: 20 },
      });
      setWaIntents(data || []);
    } catch (e) {
      // ignore
    }
  }, []);
  const loadInsights = useCallback(async () => {
    try {
      const { data } = await api.get("/insights/alerts", { params: { days: 30 } });
      setInsights(data?.insights || []);
      setInsightsTotals({ current: data?.current, previous: data?.previous });
    } catch (e) {
      // ignore
    }
  }, []);
  useEffect(() => {
    loadIntents();
    loadInsights();
    loadAutopilot();
    api.get("/settings/health").then((r) => setHealth(r.data)).catch(() => {});
    const id = setInterval(() => {
      loadIntents();
      loadInsights();
      loadAutopilot();
    }, 60000);
    return () => clearInterval(id);
  }, [loadIntents, loadInsights, loadAutopilot]);

  const markIntent = async (intentId, status) => {
    try {
      await api.put(`/whatsapp/intents/${intentId}`, { status });
      setWaIntents((prev) => prev.filter((x) => x.id !== intentId));
    } catch (e) {
      // ignore
    }
  };

  const t = stats?.totales || { costo: 0, valor_venta: 0, utilidad: 0, margen_pct: 0, items: 0, unidades: 0 };
  const ss = salesStats || { total_ventas: 0, total_utilidad: 0, margen_pct: 0, unidades_vendidas: 0, pedidos_pendientes: 0, pedidos_pagados: 0 };
  const rs = referralStats || { visitas: 0, usados: 0, conversion_pct: 0, total_descuento_otorgado: 0, ventas_referidas: 0 };
  const subs = subStats || { total: 0, activos: 0, bajas: 0 };
  const cats = stats?.por_categoria || [];

  return (
    <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-10 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10">
        <div>
          <div className="overline text-klein">Panel de control</div>
          <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-2 leading-none">
            Tu negocio,
            <br />
            <span className="text-klein">en una sola vista.</span>
          </h1>
          <p className="text-muted2 mt-4 max-w-xl">
            Inventario, ventas reales, utilidades y desempeño de publicaciones.
            Toma una foto y deja que la IA decida si comprar o vender.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/guia" className="btn-ghost" data-testid="cta-guia" data-help="Tour interactivo: aprende los 7 pasos del flujo en orden.">
            <Compass size={18} /> Cómo funciona
          </Link>
          <Link to="/escanear" className="btn-klein" data-testid="cta-scan" data-help="Toma fotos de un producto y deja que la IA lo identifique y te sugiera precios.">
            <Camera size={18} weight="bold" /> Escanear objeto
          </Link>
          <Link to="/ventas" className="btn-ghost" data-testid="cta-sales" data-help="Lista de pedidos: marca como pagados, enviados o cancelados.">
            <Receipt size={18} /> Ver pedidos
          </Link>
        </div>
      </div>

      {/* WhatsApp intent leads */}
      {waIntents.length > 0 && (
        <section
          className="mb-8 border-l-4 border-[#25D366] bg-[#F1F8E9] p-5"
          data-testid="wa-intents-panel"
        >
          <div className="flex items-start gap-3">
            <Bell size={22} weight="fill" className="text-[#1B5E20] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div>
                <div className="overline text-[#1B5E20]">Avisos · Compradores por WhatsApp</div>
                <div className="h-display text-lg sm:text-xl font-bold mt-0.5">
                  {waIntents.length} lead{waIntents.length === 1 ? "" : "s"} pendiente{waIntents.length === 1 ? "" : "s"}
                </div>
                <div className="text-xs text-muted2 mt-0.5">
                  Compradores que picaron el botón de WhatsApp en la página pública. Atiende rápido para cerrar la venta.
                </div>
              </div>
              <ul className="mt-3 space-y-2" data-testid="wa-intents-list">
                {waIntents.slice(0, 5).map((it) => (
                  <li
                    key={it.id}
                    className="flex items-center gap-2 text-sm bg-white border border-zinc-200 px-3 py-2"
                    data-testid={`wa-intent-${it.id}`}
                  >
                    <WhatsappLogo size={16} className="text-[#25D366] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{it.product_nombre}</div>
                      <div className="text-xs text-muted2">
                        {new Date(it.created_at).toLocaleString("es-MX", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </div>
                    </div>
                    <Link
                      to={`/p/${it.product_slug || it.product_id}`}
                      target="_blank"
                      className="text-klein text-xs font-medium hover:underline"
                      data-testid={`wa-intent-open-${it.id}`}
                    >
                      Ver →
                    </Link>
                    <button
                      onClick={() => markIntent(it.id, "atendido")}
                      className="text-xs text-[#1B5E20] hover:underline inline-flex items-center gap-1"
                      data-testid={`wa-intent-done-${it.id}`}
                    >
                      <Check size={12} weight="bold" /> Atendido
                    </button>
                  </li>
                ))}
              </ul>
              {waIntents.length > 5 && (
                <div className="text-[11px] text-muted2 mt-2">
                  +{waIntents.length - 5} más. Marca como atendido los que ya cerraste.
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Configuration health warning — shows missing payment channels */}
      {isAdmin && health && (() => {
        const missing = ["stripe", "whatsapp", "bank"].filter((k) => !health[k]?.ready);
        if (missing.length === 0) return null;
        return (
          <section
            className="mb-8 border border-[#FFE082] bg-[#FFF8E1] p-4"
            data-testid="config-warning"
          >
            <div className="flex items-start gap-3">
              <ShieldWarning size={18} weight="fill" className="text-[#B45309] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0 text-sm">
                <span className="font-medium text-[#7C5807]">
                  Tienes {missing.length} método{missing.length === 1 ? "" : "s"} de cobro sin configurar.
                </span>{" "}
                <span className="text-[#7C5807]">
                  Los botones desactivados NO aparecen en la página pública para evitar errores.
                </span>{" "}
                <Link
                  to="/ajustes"
                  className="text-klein font-medium hover:underline whitespace-nowrap"
                  data-testid="config-warning-link"
                >
                  Configurar ahora →
                </Link>
              </div>
            </div>
          </section>
        );
      })()}

      {/* AI Insights — actionable recommendations */}
      {insights.length > 0 && !insightsDismissed && (
        <section
          className="mb-8 border border-zinc-200"
          data-testid="insights-panel"
        >
          <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Lightbulb size={18} weight="fill" className="text-[#F59E0B]" />
              <div>
                <div className="overline">Insights · IA recomienda</div>
                <div className="text-sm font-medium mt-0.5">
                  {insights.length} recomendaci{insights.length === 1 ? "ón" : "ones"} para mover el negocio
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {insightsTotals?.current && insightsTotals?.previous && (
                <DeltaPill
                  label="Conv."
                  current={insightsTotals.current.conversion_rate}
                  previous={insightsTotals.previous.conversion_rate}
                  unit="%"
                />
              )}
              <button
                onClick={() => setInsightsDismissed(true)}
                className="text-muted2 hover:text-ink p-1"
                data-testid="insights-dismiss"
                title="Ocultar hasta la próxima recarga"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          </div>
          <ul className="divide-y divide-zinc-100">
            {insights.slice(0, 5).map((i, idx) => {
              const tone = {
                critical: { ring: "border-l-[#FF2A00]", icon: "text-[#FF2A00]" },
                warning: { ring: "border-l-[#F59E0B]", icon: "text-[#B45309]" },
                good: { ring: "border-l-[#00C853]", icon: "text-[#1B5E20]" },
                info: { ring: "border-l-zinc-300", icon: "text-muted2" },
              }[i.level] || { ring: "border-l-zinc-300", icon: "text-muted2" };
              return (
                <li
                  key={`${i.channel || "_"}-${i.type}`}
                  className={`px-5 py-4 border-l-4 ${tone.ring} flex items-start gap-3`}
                  data-testid={`insight-${i.type}-${i.channel}`}
                >
                  <Lightbulb
                    size={16}
                    weight="fill"
                    className={`mt-1 shrink-0 ${tone.icon}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold leading-tight">{i.title}</div>
                    <div className="text-xs text-muted2 mt-1 leading-relaxed">{i.message}</div>
                  </div>
                  {i.action && (
                    i.action.type === "improve_copy" ? (
                      <button
                        type="button"
                        onClick={() => runImprove(i, idx)}
                        disabled={improving?.idx === idx}
                        className="bg-klein text-white text-xs font-medium px-3 py-1.5 hover:bg-klein/90 disabled:opacity-60 whitespace-nowrap inline-flex items-center gap-1.5 mt-1"
                        data-testid={`insight-action-${idx}`}
                      >
                        {improving?.idx === idx ? (
                          <>
                            <SparkleIcon size={12} weight="fill" className="animate-pulse" />
                            Mejorando…
                          </>
                        ) : (
                          <>
                            <MagicWand size={12} weight="fill" />
                            {i.action.label}
                          </>
                        )}
                      </button>
                    ) : (
                      <Link
                        to={i.action.target}
                        className="text-klein text-xs font-medium hover:underline whitespace-nowrap inline-flex items-center gap-1 mt-1"
                        data-testid={`insight-action-${idx}`}
                      >
                        {i.action.label} <ArrowUpRight size={12} weight="bold" />
                      </Link>
                    )
                  )}
                </li>
              );
            })}
          </ul>
          {insights.length > 5 && (
            <div className="px-5 py-2 border-t border-zinc-100 text-[11px] text-muted2 text-center">
              +{insights.length - 5} más · resuelve los actuales y volvemos a calcular.
            </div>
          )}
        </section>
      )}

      {/* Autopilot summary (admin) */}
      {isAdmin && autopilot && (
        <section
          className={`mb-8 border ${autopilot.enabled ? "border-klein/30 bg-klein/[0.03]" : "border-zinc-200"}`}
          data-testid="autopilot-panel"
        >
          <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkle
                size={18}
                weight="fill"
                className={autopilot.enabled ? "text-klein" : "text-muted2"}
              />
              <div className="min-w-0">
                <div className="overline">
                  Piloto automático · {autopilot.enabled ? "Activo" : "Inactivo"}
                </div>
                <div className="text-sm font-medium mt-0.5 truncate">
                  {autopilot.enabled
                    ? autopilot.last_summary
                      ? `Última corrida: ${autopilot.last_summary.improved_count} post(s) mejorado(s)`
                      : "Esperando primera corrida"
                    : "Activa el piloto en Ajustes para que la IA mejore tus posts solos"}
                </div>
                {autopilot.next_run_at && (
                  <div className="text-[11px] text-muted2 mt-0.5">
                    Próxima corrida ~{" "}
                    {new Date(autopilot.next_run_at).toLocaleString("es-MX", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                    {" "}· cada {autopilot.interval_hours} h · max {autopilot.max_per_run}/corrida
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {autopilot.enabled && (
                <button
                  type="button"
                  onClick={runAutopilotNow}
                  disabled={runningAutopilot}
                  className="bg-klein text-white text-xs font-medium px-3 py-1.5 hover:bg-klein/90 disabled:opacity-60 inline-flex items-center gap-1.5"
                  data-testid="autopilot-run-now"
                >
                  {runningAutopilot ? (
                    <>
                      <SparkleIcon size={12} weight="fill" className="animate-pulse" />
                      Corriendo…
                    </>
                  ) : (
                    <>
                      <MagicWand size={12} weight="fill" />
                      Correr ahora
                    </>
                  )}
                </button>
              )}
              <Link
                to="/ajustes"
                className="text-klein text-xs font-medium hover:underline whitespace-nowrap"
                data-testid="autopilot-settings-link"
              >
                Configurar →
              </Link>
            </div>
          </div>
          {autopilot.last_summary && autopilot.last_summary.improved?.length > 0 && (
            <ul className="border-t border-zinc-100 divide-y divide-zinc-100" data-testid="autopilot-last-list">
              {autopilot.last_summary.improved.slice(0, 3).map((it, idx) => {
                const delta = (it.score_after || 0) - (it.score_before || 0);
                return (
                  <li
                    key={it.post_id || `${it.channel || ""}-${it.version_after || idx}`}
                    className="px-5 py-2.5 flex items-center gap-3 text-xs"
                    data-testid={`autopilot-item-${idx}`}
                  >
                    <Sparkle size={12} weight="fill" className="text-klein shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate" title={it.titulo_after}>
                        {it.titulo_after}
                      </div>
                      <div className="text-muted2 truncate">v{it.version_after} · era: {it.titulo_before}</div>
                    </div>
                    <span
                      className={`font-mono shrink-0 ${
                        delta > 0 ? "text-[#1B5E20]" : delta < 0 ? "text-[#B71C1C]" : "text-muted2"
                      }`}
                    >
                      {Math.round(it.score_before)} → {Math.round(it.score_after)}
                      {delta !== 0 && (
                        <span className="ml-1">
                          ({delta > 0 ? "+" : ""}
                          {Math.round(delta)})
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* Security alerts (admins) */}
      {isAdmin && alerts.length > 0 && !alertsDismissed && (
        <section
          className="mb-8 border-l-4 border-[#FF2A00] bg-[#FFF4F0] p-5"
          data-testid="security-alerts-panel"
        >
          <div className="flex items-start gap-3">
            <ShieldWarning size={22} weight="fill" className="text-[#FF2A00] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="overline text-[#FF2A00]">Alertas de seguridad · última hora</div>
                  <div className="h-display text-lg sm:text-xl font-bold mt-0.5">
                    {alerts.length} actividad{alerts.length === 1 ? "" : "es"} sospechosa{alerts.length === 1 ? "" : "s"} detectada{alerts.length === 1 ? "" : "s"}
                  </div>
                </div>
                <button
                  onClick={() => setAlertsDismissed(true)}
                  className="text-muted2 hover:text-ink p-1"
                  data-testid="dismiss-alerts"
                  title="Ocultar hasta la próxima recarga"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
              <ul className="mt-3 space-y-2" data-testid="security-alerts-list">
                {alerts.slice(0, 5).map((a, i) => (
                  <li
                    key={`${a.type}-${a.level || ""}-${i}`}
                    className="flex items-start gap-2 text-sm"
                    data-testid={`security-alert-${a.type}`}
                  >
                    <span
                      className={`mt-1 inline-block w-2 h-2 rounded-full shrink-0 ${
                        a.level === "critical" ? "bg-[#FF2A00]" : "bg-[#F59E0B]"
                      }`}
                    />
                    <span className="flex-1">{a.message}</span>
                    {a.link && (
                      <Link
                        to={a.link}
                        className="text-klein font-medium hover:underline whitespace-nowrap"
                        data-testid={`security-alert-link-${i}`}
                      >
                        Investigar →
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Sales KPIs */}
      <section className="mb-10">
        <div className="overline mb-3">Ventas reales</div>
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-zinc-200">
          <Kpi label="Ventas pagadas" value={fmt(ss.total_ventas)} accent testid="kpi-ventas" />
          <Kpi label="Utilidad real" value={fmt(ss.total_utilidad)} positive={ss.total_utilidad >= 0} sub={`${ss.margen_pct}% margen`} testid="kpi-utilidad-real" />
          <Kpi label="Unidades vendidas" value={ss.unidades_vendidas} testid="kpi-unidades-vendidas" />
          <Kpi label="Pedidos pendientes" value={ss.pedidos_pendientes} sub="por confirmar" testid="kpi-pendientes" />
        </div>
      </section>

      {/* Referrals KPIs */}
      <section className="mb-10">
        <div className="overline mb-3 flex items-center gap-2">
          Referidos
          <Link to="/referidos" className="text-klein hover:underline normal-case tracking-normal text-[11px]">
            ver todo →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 border-t border-l border-zinc-200">
          <Kpi label="Visitas referidas" value={rs.visitas} accent testid="kpi-ref-visitas" />
          <Kpi label="Conversión" value={`${rs.conversion_pct}%`} sub="código → venta" testid="kpi-ref-conv" />
          <Kpi label="Ventas referidas" value={rs.ventas_referidas} testid="kpi-ref-ventas" />
          <Kpi label="Descuento otorgado" value={fmt(rs.total_descuento_otorgado)} testid="kpi-ref-desc" sub={`${rs.usados} códigos usados`} />
          <Kpi
            label="Suscriptores"
            value={subs.activos}
            sub={`${subs.total} totales · ${subs.bajas} bajas`}
            testid="kpi-suscriptores"
          />
        </div>
      </section>

      {/* Inventory KPIs */}
      <section className="mb-10">
        <div className="overline mb-3">Inventario</div>
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-zinc-200">
          <Kpi label="Valor en stock" value={fmt(t.valor_venta)} testid="kpi-valor" />
          <Kpi label="Costo total" value={fmt(t.costo)} testid="kpi-costo" />
          <Kpi label="Utilidad potencial" value={fmt(t.utilidad)} positive={t.utilidad >= 0} testid="kpi-pot" />
          <Kpi label="Margen potencial" value={`${t.margen_pct}%`} testid="kpi-margen" sub={`${t.items} ítems · ${t.unidades} u.`} />
        </div>
      </section>

      {/* Chart + Top categories */}
      <section className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-zinc-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Inventario por categoría</div>
              <div className="h-display text-2xl font-bold mt-1">Distribución del valor</div>
            </div>
            <TrendUp size={22} className="text-klein" />
          </div>
          <div className="h-72">
            {cats.length === 0 ? (
              <Empty message="Aún no hay productos. Empieza escaneando un objeto." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cats}>
                  <CartesianGrid stroke="#e4e4e7" vertical={false} />
                  <XAxis dataKey="categoria" tick={{ fontSize: 11, fill: "#52525B" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#52525B" }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 0, border: "1px solid #09090B", fontSize: 12 }}
                    formatter={(v) => fmt(v)}
                  />
                  <Bar dataKey="valor_venta" fill="#002FA7" />
                  <Bar dataKey="utilidad" fill="#00C853" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="border border-zinc-200 p-6">
          <div className="overline">Recientes</div>
          <div className="h-display text-2xl font-bold mt-1 mb-4">Pedidos</div>
          {sales.length === 0 ? (
            <Empty message="Aún no hay pedidos. Comparte tu link público." />
          ) : (
            <ul className="divide-y divide-zinc-200">
              {sales.slice(0, 5).map((s) => (
                <li key={s.id} className="py-3 flex items-start gap-3" data-testid={`recent-sale-${s.id}`}>
                  {s.canal === "whatsapp" ? <WhatsappLogo size={16} className="text-[#25D366] mt-0.5" /> : <Receipt size={16} className="text-klein mt-0.5" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{s.product_nombre || "—"}</div>
                    <div className="text-xs text-muted2 mt-0.5">
                      {s.comprador_nombre} · {fmt(s.total)} · {s.estado}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Link to="/ventas" className="mt-4 inline-flex items-center gap-1 text-sm text-klein font-medium" data-testid="link-all-sales">
            Ver todos <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Posts strip */}
      {posts.length > 0 && (
        <section className="mt-10 border border-zinc-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline">Publicaciones</div>
              <div className="h-display text-2xl font-bold mt-1">Recientes</div>
            </div>
            <Link to="/publicaciones" className="text-sm text-klein font-medium inline-flex items-center gap-1">
              Ver todas <ArrowRight size={14} />
            </Link>
          </div>
          <ul className="mt-4 divide-y divide-zinc-100">
            {posts.slice(0, 4).map((p) => (
              <li key={p.id} className="py-3 flex items-center gap-3">
                <MegaphoneSimple size={16} className="text-klein" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{p.titulo}</div>
                  <div className="text-xs text-muted2">
                    {p.plataforma} · v{p.version} · score {p.score}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Feature strip */}
      <section className="mt-12 grid grid-cols-1 md:grid-cols-3 border-t border-l border-zinc-200">
        <Feature icon={<Camera size={22} />} title="Reconocimiento por foto" desc="GPT-5.2 Vision identifica el objeto, su uso y rango de precios nuevo / usado / oportunidad." />
        <Feature icon={<Package size={22} />} title="Inventario con utilidades" desc="Costos, ventas y márgenes por categoría. Sin hojas de cálculo." />
        <Feature icon={<Sparkle size={22} />} title="Publicaciones que mejoran solas" desc="Genera copy listo para redes y reescribe automáticamente si las métricas no convencen." />
      </section>

      {/* AI improvement result modal */}
      {improveResult && (
        <ImproveResultModal
          result={improveResult}
          onClose={() => setImproveResult(null)}
        />
      )}
    </div>
  );
}
